// src/components/dashboard/EditModePanel.tsx
import { useState } from 'react';
import type { BookingJob, GearItem } from '@/lib/types/workflow';
import { brand, fonts, motion, styles } from '@/lib/tokens';
import { ExpandOverlay } from '@/components/ui/ExpandOverlay';

type Props = {
  booking: BookingJob;
  onQtyChange: (id: string, qty: number) => void;
  onUnitPriceChange: (id: string, price: number) => void;
  onSwap: (id: string) => void;
  onRemove: (id: string) => void;
};

export function EditModePanel({
  booking,
  onQtyChange,
  onUnitPriceChange,
  onSwap,
  onRemove,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <section
        className="flex flex-col overflow-hidden panel-col"
        style={{
          display: 'flex',
          flexDirection: 'column',
          borderRight: `1px solid ${brand.hairlineSoft}`,
          overflow: 'hidden',
          background: 'rgba(0,0,0,0.15)',
        }}
      >
        {/* Panel header */}
        <div
          className="panel-header"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span>Line Item Extraction</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ ...styles.monoLabel }}>
              {booking.gear.length} Items
            </span>
            <button
              onClick={() => setExpanded(true)}
              aria-label="Expand line items"
              className="expand-trigger"
              style={{ ...styles.iconBtn, transition: `color ${motion.fast}` }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = brand.onSurface;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = brand.monoDim;
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                open_in_full
              </span>
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div
          className="custom-scroll"
          style={{ flex: 1, overflowY: 'auto', padding: 20 }}
        >
          <GearEditorContent
            booking={booking}
            onQtyChange={onQtyChange}
            onUnitPriceChange={onUnitPriceChange}
            onSwap={onSwap}
            onRemove={onRemove}
          />
        </div>
      </section>

      <ExpandOverlay
        open={expanded}
        title="Line Item Extraction"
        subtitle={`${booking.gear.length} items · ${booking.clientName}`}
        onClose={() => setExpanded(false)}
      >
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <GearEditorContent
            booking={booking}
            onQtyChange={onQtyChange}
            onUnitPriceChange={onUnitPriceChange}
            onSwap={onSwap}
            onRemove={onRemove}
          />
        </div>
      </ExpandOverlay>
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared gear editor content — rendered both inline and inside the overlay
// ---------------------------------------------------------------------------

function GearEditorContent({
  booking,
  onQtyChange,
  onUnitPriceChange,
  onSwap,
  onRemove,
}: Omit<Props, 'booking'> & { booking: BookingJob }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {booking.gear.map((g) => (
        <GearRow
          key={g.id}
          item={g}
          onQtyChange={onQtyChange}
          onUnitPriceChange={onUnitPriceChange}
          onSwap={onSwap}
          onRemove={onRemove}
        />
      ))}

      <button
        style={{
          marginTop: 12,
          width: '100%',
          padding: '10px 16px',
          background: 'transparent',
          border: `1px dashed ${brand.hairlineSoft}`,
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: brand.monoDim,
          cursor: 'pointer',
          fontFamily: fonts.sans,
          fontSize: 13,
          transition: `background ${motion.fast}`,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(var(--fg-rgb),0.05)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
          add_circle
        </span>
        Add Item
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gear row
// ---------------------------------------------------------------------------

function GearRow({
  item,
  onQtyChange,
  onSwap,
  onRemove,
}: {
  item: GearItem;
  onQtyChange: (id: string, qty: number) => void;
  onUnitPriceChange: (id: string, price: number) => void;
  onSwap: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  const stockColor =
    item.stockStatus === 'IN_STOCK'    ? brand.success  :
    item.stockStatus === 'LOW_STOCK'   ? brand.warning  :
    item.stockStatus === 'SUBSTITUTION'? brand.warning  :
    brand.error;

  return (
    <div
      className="card-border"
      style={{
        padding: 12,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: brand.hairlineSoft,
      }}
    >
      {/* Qty input */}
      <input
        type="number"
        value={item.quantity}
        onChange={(e) => onQtyChange(item.id, Math.max(1, Number(e.target.value) || 1))}
        style={{
          ...styles.inputBase,
          width: 48,
          textAlign: 'center',
          padding: '4px 0',
        }}
      />

      {/* Item select */}
      <select
        style={{
          ...styles.inputBase,
          flex: 1,
          fontFamily: fonts.sans,
          fontSize: 13,
          padding: '6px 8px',
          appearance: 'none',
          cursor: 'pointer',
        }}
        value={item.name}
        onChange={() => onSwap(item.id)}
      >
        <option value={item.name}>{item.name}</option>
        {item.substitutionNote && (
          <option value="substitute">Alternative available</option>
        )}
      </select>

      {/* Stock status */}
      <span
        style={{
          ...styles.monoLabel,
          color:      stockColor,
          fontSize:   9,
          letterSpacing: '0.06em',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {item.stockStatus.replace('_', ' ')}
      </span>

      {/* Delete */}
      <button
        onClick={() => onRemove(item.id)}
        style={{
          ...styles.iconBtn,
          color:      brand.monoDarker,
          transition: `color ${motion.default}`,
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.color = brand.error;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.color = brand.monoDarker;
        }}
        aria-label="Remove item"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
          delete
        </span>
      </button>
    </div>
  );
}
